import { Core } from "@faire/slate/foundation/color";
import { SVGIconProps } from "@faire/slate-icons/types";
import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

import { AvatarIconPosition, AvatarIconSize } from "./types";

export interface IAvatarIconProps {
  /**
   * The icon component to render
   */
  Component: React.FC<SVGIconProps>;

  /**
   * The position of the icon.
   *
   * @default "topRight"
   */
  position?: AvatarIconPosition;

  /**
   * Whether to show the icon container.
   *
   * @default false
   */
  showContainer?: boolean;
}

type IProps = IAvatarIconProps & {
  /**
   * Icon container className
   */
  className?: string;

  /**
   * The size of the component (including padding and border)
   */
  size: AvatarIconSize;
};

export const AvatarIcon: React.FC<IProps> = ({
  Component: Icon,
  position = "topRight",
  showContainer,
  size,
}) => {
  const containerCls = containerStyle.cls({
    showContainer: !!showContainer,
    position,
    size,
  });

  return (
    <span className={containerCls}>
      <Icon
        style={{ width: "100%", height: "100%" }}
        color={Core.icon.primary}
      />
    </span>
  );
};

const containerStyle = tsv({
  base: ["fs-avatar__icon"],
  variants: {
    position: {
      topRight: "bottom-[unset] left-[unset]",
      bottomRight: "top-[unset] left-[unset]",
    } satisfies Record<AvatarIconPosition, string>,
    showContainer: {
      false: ["border-0", "bg-transparent"],
    },
    size: {
      small: "size-(--slate-dimensions-4)",
      medium: "size-(--slate-dimensions-5)",
      large: "size-(--slate-dimensions-6)",
    } satisfies Record<AvatarIconSize, string>,
  },
  compoundVariants: [
    {
      showContainer: true,
      size: "small",
      className: "p-[--spacing(0.75)]",
    },
    {
      showContainer: true,
      size: "medium",
      className: "p-[--spacing(0.75)]",
    },
    {
      showContainer: true,
      size: "large",
      className: "p-[--spacing(1.25)]",
    },
  ],
});
